import { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import { getContextPermissions, type ContextPermissions } from '../api/contextApi';
import {
  subscribeToPermissionInvalidation,
  getPermissionEpoch,
  bumpPermissionEpoch,
  identifyClient,
  registerPermissionCoalescingMap,
} from './permissionInvalidation';

export interface UseContextPermissionsReturn extends ContextPermissions {
  loading: boolean;
  error: string | null;
}

const FAIL_CLOSED: ContextPermissions = { canEdit: false, canDelete: false, canShare: false };

interface InFlightEntry {
  /**
   * The `schemaClient` the request was issued with, compared by REFERENCE.
   *
   * This is NOT what makes an impersonation switch safe, despite the obvious
   * reading. `ApiClientsProvider` deliberately does not rebuild its clients on
   * impersonation — the bearer token is resolved per request through
   * `getEffectiveToken`, so the reference is stable across identities and this
   * check would never fire for that case. Cross-identity safety comes from the
   * shared invalidation epoch. (This docblock previously asserted the opposite;
   * `useSchemaTypeCacheReset`'s own comment falsifies it.)
   *
   * What the reference check does buy: it stops a request issued by a client
   * that has since been REBUILT (logout, tenant change) from being adopted by
   * a later mount.
   */
  client: unknown;
  request: Promise<ContextPermissions>;
}

// Module-scope: dedupes CONCURRENT fetches for one context so two mounts issue
// one request, not two. Entries are removed as soon as the request settles, so
// this holds no answers between renders — it is a request coalescer, not a
// result cache. The decisions themselves live in each mount's state, which is
// why invalidation needs the shared epoch and not just a clear of this map.
const inFlightByContext = new Map<string, InFlightEntry>();
// Registered so ANY epoch bump drops these requests, not just this hook's own
// reset — a shared epoch means another hook's reset re-runs this hook's effect.
registerPermissionCoalescingMap(inFlightByContext);

/** A permission decision together with the exact key it was resolved FOR. */
interface ResolvedDecision {
  key: string;
  permissions: ContextPermissions;
  error: string | null;
}

/**
 * Invalidates every mounted caller's decision and drops in-flight requests.
 * Call on logout / impersonation switch.
 *
 * This used to clear `inFlightByContext` and nothing else, which invalidated
 * NOTHING: entries are deleted the moment a request settles, so in the steady
 * state the map is already empty. The decision lives in each mount's state, and
 * `ApiClientsProvider` does not rebuild its clients on impersonation, so this
 * hook's deps did not change either — the acting identity simply inherited the
 * previous identity's `canEdit` on the context. Same defect as EAINT-789 D-4
 * fixed for the view-level twin; found in review pass 4 because the twin was
 * fixed and this one was not.
 */
export function resetContextPermissionsCache(): void {
  inFlightByContext.clear();
  bumpPermissionEpoch();
}

/**
 * Fetches permission decisions for the current user on a context — the
 * resource `Edit in context` (EAINT-781 D-G) gates on, distinct from
 * `context_view.edit` (see `useContextViewPermissions`).
 *
 * Fail-closed: any network or SpiceDB error results in all-false permissions
 * so the UI conservatively disables edit/delete/share actions rather than
 * exposing them.
 *
 * Supersedes `useCanEditContext` — same shape, plus the barrel export and the
 * module-scope dedup this hook's view-scoped sibling already had.
 *
 * Requires the backend route (org scoped server-side from the token):
 *   GET /contexts/system_name/:systemName/permissions
 */
export function useContextPermissions(
  apiClients: ApiClients | null,
  systemName: string | null | undefined
): UseContextPermissionsReturn {
  /**
   * The decision AND the key it was resolved for, in ONE state value — see the
   * view-level twin for the full rationale. In short: `loading` is DERIVED from
   * the key, so the two must not be separately writable, and deriving it is
   * what stops a `useState(false)` flag reporting `{ loading: false,
   * canEdit: false }` — "no permission" rather than "checking…" — on the first
   * committed render and for one frame after an identity switch.
   */
  const [decision, setDecision] = useState<ResolvedDecision | null>(null);

  const isMountedRef = useRef(true);
  const epoch = useSyncExternalStore(
    subscribeToPermissionInvalidation,
    getPermissionEpoch,
    getPermissionEpoch
  );

  const schemaClient = apiClients?.schemaClient ?? null;
  const hasTarget = Boolean(schemaClient && systemName);
  // The CLIENT is part of the identity, not just the context and the epoch:
  // all three re-trigger the decision, and a key omitting the client stays
  // equal across a client rebuild while the effect re-runs.
  const resolutionKey =
    hasTarget && schemaClient
      ? JSON.stringify([identifyClient(schemaClient as object), systemName, epoch])
      : null;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!schemaClient || !systemName) {
      setDecision(null);
      return;
    }

    let cancelled = false;
    // Nothing is blanked before the fetch: a stale decision cannot be surfaced
    // anyway, because its key no longer matches and the derived value below
    // returns FAIL_CLOSED for it. Switching from a context the caller can edit
    // to one they cannot is covered by the key changing, in the render that
    // changes it.
    const existing = inFlightByContext.get(systemName);
    // Reference equality, not a serialised key: a different client means a
    // different identity, and its answer must never be reused.
    let request = existing?.client === schemaClient ? existing.request : undefined;
    if (!request) {
      const issued = getContextPermissions(schemaClient, systemName);
      request = issued;
      inFlightByContext.set(systemName, { client: schemaClient, request: issued });
      // Side branch for cache cleanup only — swallow here so a rejection
      // doesn't surface as an unhandled rejection; the `request` variable
      // below still carries the real error to each mount's own .catch().
      issued
        .finally(() => {
          // Only clear the entry we created; a newer client's request may have
          // replaced it while this one was still settling.
          if (inFlightByContext.get(systemName)?.request === issued) {
            inFlightByContext.delete(systemName);
          }
        })
        .catch(() => undefined);
    }

    request
      .then((result) => {
        if (!cancelled && isMountedRef.current && resolutionKey) {
          setDecision({ key: resolutionKey, permissions: result, error: null });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && isMountedRef.current && resolutionKey) {
          // A refusal IS a resolved answer — settle, or the caller reads
          // `loading: true` forever and never learns the request finished.
          setDecision({
            key: resolutionKey,
            permissions: FAIL_CLOSED,
            error: err instanceof Error ? err.message : 'Failed to fetch context permissions',
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // `epoch` is not read in the body — it is the invalidation signal that
    // re-runs this effect when `resetContextPermissionsCache` fires.
    // `resolutionKey` is derived from the client, `systemName` and `epoch`, so
    // it adds no new trigger.
  }, [schemaClient, systemName, epoch, resolutionKey]);

  // Derived, never stored: a key mismatch means the decision belongs to a
  // different context, a different client, or a pre-invalidation identity, so
  // it must not be surfaced even for the single render before the effect
  // re-runs.
  const resolved = hasTarget && decision?.key === resolutionKey ? decision : null;

  return {
    ...(resolved ? resolved.permissions : FAIL_CLOSED),
    loading: hasTarget && !resolved,
    error: resolved ? resolved.error : null,
  };
}

export default useContextPermissions;
